import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Lock,
  Server,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  Database,
  Cpu,
  Zap,
  Smartphone,
  FileText,
  Eye,
  FileCheck,
  Layers,
  Settings,
  ShieldCheck,
  MessageSquare,
} from "lucide-react";
import MobileDemo from "@/components/MobileDemo";
import { lazy, Suspense } from "react";
const ProductTour = lazy(() =>
  import("@/components/ProductTour").then(m => ({ default: m.ProductTour }))
);
import { Link } from "wouter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import faqs from "../data/faqs.json";
import SEO from "@/components/SEO";
import { useTranslation, Trans } from "react-i18next";

export default function Home() {
  const { t } = useTranslation();

  const homeSchema = [
    {
      "@type": "SoftwareApplication",
      name: "SoverX.ai",
      applicationCategory: "BusinessApplication",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "BRL",
      },
      operatingSystem: "Web, Windows, Linux, macOS",
      description:
        "Plataforma de Soberania da IA. Transforme dados corporativos em inteligência estratégica proprietária, com total controle, segurança e governança on-premise.",
      screenshot: "https://soverx.ai/images/product-hero.png",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5",
        ratingCount: "1",
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: (faqs.home || []).map(faq => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
    {
      "@type": "Service",
      serviceType: "IA Corporativa Segura",
      provider: {
        "@type": "Organization",
        name: "SoverX.ai",
      },
      areaServed: "BR",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Soberania da IA e Inteligência Corporativa Segura | SoverX.ai"
        description="SoverX.ai: Plataforma de Soberania da IA. Transforme dados corporativos em inteligência estratégica proprietária, com total controle, segurança e governança on-premise."
        canonical="/"
        type="website"
        image="/logo/og.jpg"
        imageWidth="1200"
        imageHeight="630"
        keywords="IA corporativa offline, governança de IA, auditoria de IA, RAG com citações, logs WORM, controle de acesso ACL, SSO corporativo, IA para ambientes regulados, segurança de dados, chatbot corporativo seguro"
        schema={homeSchema}
        imageAlt="SoverX.ai - Plataforma de Soberania da IA"
        breadcrumbs={[{ name: t("nav.home"), item: "/" }]}
      />

      {/* 1) Hero Section */}
      <section className="relative pt-20 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/grid-pattern.svg')] bg-cover bg-center opacity-20 pointer-events-none mix-blend-screen" />
        <div className="absolute inset-0 bg-linear-to-b from-background via-background/80 to-background pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-linear-to-l from-primary/10 to-transparent pointer-events-none" />

        <div className="container relative z-10">
          <div className="max-w-5xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-1000">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm font-medium text-primary">
                {t("hero.badge")}
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 leading-tight animate-in fade-in slide-in-from-bottom-8 duration-700">
              <Trans i18nKey="hero.title">
                {"Sua IA deve ser um ativo "}
                <span className="text-transparent bg-clip-text bg-linear-to-r from-primary to-cyan-400 pb-1">
                  soberano e estratégico
                </span>
                {", não um serviço alugado."}
              </Trans>
            </h1>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300 fill-mode-backwards">
              {t("hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-6 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-500 fill-mode-backwards">
              <Link href="/solicitar-demo">
                <Button
                  size="lg"
                  className="text-lg px-8 h-14 shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all"
                >
                  {t("hero.cta_primary")}
                </Button>
              </Link>
              <a
                href="/checklist.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-8 h-14 border-primary/20 hover:bg-primary/5 w-full sm:w-auto"
                >
                  <FileText className="mr-2 h-5 w-5" />{" "}
                  {t("hero.cta_secondary")}
                </Button>
              </a>
            </div>
            <p className="text-sm text-muted-foreground mb-12 italic">
              {t("hero.go_live")}
            </p>

            {/* Trusted By */}
            <div className="pt-8 border-t border-border/30 mb-12">
              <p className="text-sm text-muted-foreground mb-6 font-medium tracking-wide">
                {t("hero.trusted_by")}
              </p>
              <div className="flex flex-wrap items-center gap-8 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
                <img
                  src="/trusted/aws.png"
                  alt="AWS"
                  loading="lazy"
                  decoding="async"
                  width="80"
                  height="32"
                  className="h-8 w-auto object-contain"
                />
                <img
                  src="/trusted/azure.png"
                  alt="Microsoft Azure"
                  loading="lazy"
                  decoding="async"
                  width="100"
                  height="32"
                  className="h-8 w-auto object-contain"
                />
                <img
                  src="/trusted/vlz.png"
                  alt="Valorizza"
                  loading="lazy"
                  decoding="async"
                  width="100"
                  height="32"
                  className="h-8 w-auto object-contain"
                />
                <span className="text-xs text-muted-foreground border border-border/30 px-3 py-1 rounded-full bg-muted/50">
                  {t("hero.coming_soon")}
                </span>
                <span className="text-xs text-muted-foreground border border-border/30 px-3 py-1 rounded-full bg-muted/50">
                  {t("hero.coming_soon")}
                </span>
              </div>
            </div>

            {/* Chips */}
            <div className="flex flex-wrap gap-3">
              {[
                "RAG com citações",
                "SSO/AD + ACL",
                "Logs WORM/retention",
                "Customer-Managed",
                "Go-live 2 semanas",
              ].map((chip, i) => (
                <Badge
                  key={i}
                  variant="secondary"
                  className="bg-card border border-border/50 px-3 py-1 text-sm font-normal text-muted-foreground"
                >
                  {chip}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Product Tour */}
      <section className="container -mt-10 relative z-20 mb-20">
        <Suspense fallback={null}>
          <ProductTour />
        </Suspense>
      </section>

      {/* Stats Grid */}
      <section className="container -mt-10 relative z-20 mb-20">
        <div className="grid md:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-700 fill-mode-backwards">
          <Card className="bg-card/50 backdrop-blur border-primary/20 hover:border-primary/50 transition-colors">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {t("stats.zero_incidents")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("stats.zero_incidents_desc")}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur border-primary/20 hover:border-primary/50 transition-colors">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-cyan-500/10 flex items-center justify-center">
                <Zap className="h-6 w-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{t("stats.search_time")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("stats.search_time_desc")}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur border-primary/20 hover:border-primary/50 transition-colors">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{t("stats.comm_issues")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("stats.comm_issues_desc")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 2) Problem */}
      <section className="py-20 bg-card/30 border-y border-border/50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{t("problem.title")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("problem.description")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300 fill-mode-backwards">
            <Card className="bg-background border-border hover:border-red-500/30 transition-colors">
              <CardHeader>
                <AlertTriangle className="h-10 w-10 text-red-500 mb-2" />
                <CardTitle className="text-lg">
                  {t("problem.card1_title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t("problem.card1_desc")}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-background border-border hover:border-orange-500/30 transition-colors">
              <CardHeader>
                <Layers className="h-10 w-10 text-orange-500 mb-2" />
                <CardTitle className="text-lg">
                  {t("problem.card2_title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t("problem.card2_desc")}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-background border-border hover:border-yellow-500/30 transition-colors">
              <CardHeader>
                <Eye className="h-10 w-10 text-yellow-500 mb-2" />
                <CardTitle className="text-lg">
                  {t("problem.card3_title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t("problem.card3_desc")}
                </p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-12">
            <p className="text-xl font-medium text-primary">
              {t("problem.conclusion")}
            </p>
          </div>
        </div>
      </section>

      {/* 3) How It Works */}
      <section className="container py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">{t("how_it_works.title")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 relative">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-primary/20 via-primary/50 to-primary/20 z-0" />
          {[
            { icon: Database, key: "step1" },
            { icon: Cpu, key: "step2" },
            { icon: CheckCircle2, key: "step3" },
          ].map(({ icon: Icon, key }, i) => (
            <div
              key={i}
              className="relative z-10 flex flex-col items-center text-center group"
            >
              <div className="w-24 h-24 rounded-full bg-card border border-border flex items-center justify-center mb-6 shadow-lg group-hover:border-primary/50 group-hover:shadow-primary/20 transition-all duration-300">
                <Icon className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">
                {t(`how_it_works.${key}_title`)}
              </h3>
              <p className="text-muted-foreground max-w-xs">
                {t(`how_it_works.${key}_desc`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 4) Differentials */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                {t("differentials.title")}
              </h2>
              <p className="text-muted-foreground mb-8 text-lg">
                {t("differentials.description")}
              </p>
              <ul className="space-y-6">
                {[
                  {
                    icon: Server,
                    title: t("differentials.item1_title"),
                    desc: t("differentials.item1_desc"),
                  },
                  {
                    icon: Lock,
                    title: t("differentials.item2_title"),
                    desc: t("differentials.item2_desc"),
                  },
                  {
                    icon: FileCheck,
                    title: t("differentials.item3_title"),
                    desc: t("differentials.item3_desc"),
                  },
                  {
                    icon: Settings,
                    title: t("differentials.item4_title"),
                    desc: t("differentials.item4_desc"),
                  },
                ].map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <div className="mt-1 bg-primary/10 p-2 rounded-lg h-fit">
                      <item.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Admin Panel Preview */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-cyan-500/20 rounded-xl blur-xl opacity-50" />
              <Card className="relative bg-card border-border shadow-2xl">
                <CardHeader className="border-b border-border/50 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      admin-panel.soverx.local
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">
                      {t("differentials.dashboard_title")}
                    </p>
                    <Badge
                      variant="outline"
                      className="text-green-500 border-green-500/30 bg-green-500/10"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />{" "}
                      {t("differentials.system_secure")}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">
                        {t("differentials.blocked_attempts")}
                      </div>
                      <div className="text-xl font-bold text-red-500">0</div>
                    </div>
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">
                        {t("differentials.indexed_docs")}
                      </div>
                      <div className="text-xl font-bold">1.2M</div>
                    </div>
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">
                        {t("differentials.audited_queries")}
                      </div>
                      <div className="text-xl font-bold">45k</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-sm font-medium">
                      {t("differentials.active_controls")}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />{" "}
                      {t("differentials.control1")}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />{" "}
                      {t("differentials.control2")}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />{" "}
                      {t("differentials.control3")}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />{" "}
                      {t("differentials.control4")}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* 5) Trust & Security */}
      <section className="py-20 bg-background border-y border-border/50">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <Badge
              variant="outline"
              className="mb-4 border-primary/50 text-primary bg-primary/5"
            >
              {t("trust.badge")}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t("trust.title")}
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              {t("trust.description")}
            </p>
            <div className="grid md:grid-cols-2 gap-6 text-left max-w-3xl mx-auto">
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                <h3 className="font-semibold">{t("differentials.control5")}</h3>
              </div>
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                <h3 className="font-semibold">{t("differentials.control6")}</h3>
              </div>
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                <h3 className="font-semibold">{t("differentials.control7")}</h3>
              </div>
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                <h3 className="font-semibold">{t("differentials.control8")}</h3>
              </div>
            </div>
            <div className="mt-10">
              <Link href="/produto">
                <Button variant="outline" className="gap-2">
                  <Shield className="h-4 w-4" /> {t("trust.cta")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 6) Mobile & Whitelabel */}
      <section className="py-20 bg-muted/30 border-y border-border/50">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <MobileDemo />
            </div>
            <div className="order-1 md:order-2">
              <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20 border-none">
                {t("mobile.badge")}
              </Badge>
              <h2 className="text-3xl font-bold mb-6">{t("mobile.title")}</h2>
              <p className="text-muted-foreground text-lg mb-8">
                {t("mobile.description")}
              </p>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-lg bg-background border border-border flex items-center justify-center shrink-0">
                    <Settings className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">
                      {t("mobile.whitelabel_title")}
                    </h3>
                    <p className="text-muted-foreground">
                      {t("mobile.whitelabel_desc")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-lg bg-background border border-border flex items-center justify-center shrink-0">
                    <Smartphone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">
                      {t("mobile.app_title")}
                    </h3>
                    <p className="text-muted-foreground">
                      {t("mobile.app_desc")}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-10">
                <Link href="/solicitar-demo">
                  <Button size="lg" variant="outline" className="gap-2">
                    {t("mobile.cta")} <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7) FAQ */}
      <section className="container py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">{t("faq.title")}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("faq.description")}
          </p>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqs.home.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 8) Final CTA */}
      <section className="container py-20">
        <div className="bg-primary/5 border border-primary/20 rounded-3xl p-12 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('/images/grid-pattern.svg')] opacity-5 pointer-events-none" />
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t("final_cta.title")}
            </h2>
            <p className="text-xl text-muted-foreground mb-10">
              {t("final_cta.description")}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link href="/solicitar-demo">
                <Button
                  size="lg"
                  className="text-lg px-8 h-14 shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all"
                >
                  {t("hero.cta_primary")}
                </Button>
              </Link>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="text-lg px-8 h-14 bg-background/50"
              >
                <Link href="/produto">{t("common.see_plans")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
